import { useEffect, useRef, useState } from 'react';
import type { ActiveViewer, Kind } from '@sflx/shared';
import { useActiveViewers } from '../hooks/useActiveViewers';
import { formatRelative, pluralize } from '../lib/format';
import { Icon } from './Icon';

/**
 * Header indicator for "who is streaming right now" -- a status readout, not
 * an alert, so unlike ErrorBadge it stays visible at zero rather than
 * disappearing. Clicking it expands a panel of who, and what they're
 * watching.
 */
export function ViewerBadge({ enabled }: { enabled: boolean }) {
  const { viewers } = useActiveViewers(enabled);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Closing the popover when the count changes to zero would be surprising
  // mid-look, so this only resets when the feature itself is disabled.
  useEffect(() => {
    if (!enabled) setOpen(false);
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div className="viewer-badge" ref={rootRef}>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label={`${viewers.length} ${pluralize(viewers.length, 'person', 'people')} streaming now`}
        title={`${viewers.length} streaming now`}
      >
        <Icon name="eye" size={14} />
        {viewers.length}
      </button>

      {open ? (
        <div className="viewer-badge__panel card stack stack--tight" role="dialog" aria-label="Who's streaming">
          <h3>Streaming now</h3>
          {viewers.length === 0 ? (
            <p className="muted">No one is streaming right now.</p>
          ) : (
            <ul className="viewer-badge__list">
              {viewers.map((viewer) => (
                <ViewerRow key={viewer.userId} viewer={viewer} />
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

function ViewerRow({ viewer }: { viewer: ActiveViewer }) {
  return (
    <li className="viewer-badge__row">
      <div className="viewer-badge__email">{viewer.email}</div>
      <div className="muted viewer-badge__what">
        {kindLabel(viewer.kind)} · {viewer.title}
        <span className="viewer-badge__account"> · {viewer.accountLabel}</span>
      </div>
      <div className="viewer-badge__since">since {formatRelative(viewer.startedAt)}</div>
    </li>
  );
}

function kindLabel(kind: Kind): string {
  if (kind === 'live') return 'Live';
  if (kind === 'movie') return 'Movie';
  return 'Series';
}
